import React, { Component } from "react";
import { FormattedMessage } from "react-intl";
import Toggle from "react-toggle";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";

import { SHIFTS_IN_WEEK } from "@/lib/constants";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import { getBackend } from "@/lib/backend/backend2";
import * as u from "@/lib/util";
import local_settings from "@/lib/localSettings";
import * as t from "@/lib/backend/manufacturing2.generated.types";
import { getLineTitleCssClass, getShiftCellStatusCssClass } from "./shiftAssignment";
import * as bl from "@/lib/businessLogic";
import { exportWeekToHtml } from "./ShiftAssignmentDetailsExportWeek";
import { exportDayToHtml } from "./ShiftAssignmentDetailsExportDay";
import ShiftAssignmentDetailsExportDayModal from "./ShiftAssignmentDetailsExportDayModal";
import { translate, hasTranslation } from "@/modules/shared/IntlProviderWrapper";
import Loader from "@/modules/shared/ui/Loader";
import ShiftAssignmentPoolEdit from "./ShiftAssignmentPoolEdit";
import type { PoolEditModalType } from "./ShiftAssignmentPoolEdit";
import { niceNumber, shiftTag, positiveModulo } from "@/lib/util";
import {
    LINE_TAGS,
    LINE_TYPES,
    LINE_GROUP_TAGS,
    PLANNING_MODE,
    COPY_WEEKLY_ASSIGNMENTS_STRATEGY,
    LINE_TAGS_ACCESS,
    LINE_GROUP_TAGS_ACCESS,
    PLANT_TAGS_ACCESS,
    type LineTypesEnum,
} from "@/lib/ManufacturingTags.generated";
import {
    sumWorkerHoursByShift,
    partitionLineUuidsByPoolSchedulingRole,
    partitionLinesByPoolSchedulingRole,
    findPoolLine,
    postProcessLines,
    lineGroupUsesAdvancedPoolScheduling,
    isPoolModePoolLine,
    isPoolModeSchedulingLine,
    mapShiftPersonLinesToStoreRecs,
} from "../core/shiftBoard";
import type { IPeopleScheduleTemplate } from "@/lib/backend/manufacturing2.generated.types";
import { ShiftAssignmentWeekPoolModal } from "../weekPool/ShiftAssignmentWeekPool";
import * as Auth from "@/lib/auth";
import { arePeopleSchedulesEqual } from "@/lib/shifts";

import img_edit from "@/img/edit.svg";
import { IGNORE_COMPETENCE_FLAG } from "@/modules/shared/flags";

const STORAGE_PREFIX = "people-shift-daily-export";

type CanPersonWorkInShiftFunction = (person_uuid: string, shift_number: number) => boolean;
type CanPersonWorkOnLineFunction = (person_uuid: string, line_uuid: string) => boolean;
type GetUniquePersonNameFunction = (person_uuid: string) => string;
type GetIsPersonAdjusterFunction = (person_uuid: string) => boolean;
type GetPersonCompetenceLevelFunction = (person_uuid: string, line_uuid: string) => number;
type PersonMaxNumberShifts = (person_uuid: string) => number;
type PersonWorks12Hours = (person_uuid: string) => boolean;

type ShiftStatistics = {
    scheduled_lines: number;
    active_lines: number;
};

type TemplateLinesMap = Map<string, Set<string>[]>;

type Props = {
    selectedPerson: t.IPersonData | null;
    year: number;
    week: number;
    plant: string;
    line_group_uuid: string;
    uniquePersonName: GetUniquePersonNameFunction;
    isPersonAdjuster: GetIsPersonAdjusterFunction;
    canPersonWorkOnLine: CanPersonWorkOnLineFunction;
    canPersonWorkInShift: CanPersonWorkInShiftFunction;
    personMaxNumberShifts: PersonMaxNumberShifts;
    personAllowedShifts: bl.PersonAllowedShifts;
    personWorks12Hours: PersonWorks12Hours;
    getPersonCompetenceLevel: GetPersonCompetenceLevelFunction;
    people: t.IPersonData[];
    template_mode: boolean;
    template: IPeopleScheduleTemplate | null;
    isLoading: boolean;
    onPersonClick: (person_uuid: string) => void;
    onScheduleUpdate: (lines: t.IShiftPersonLineRec[]) => void;
    onShiftsSaved: () => void;
    onShiftSaveStateChange: (inProgress: boolean) => void;
    onTemplateChange: (template: IPeopleScheduleTemplate | null) => void;
    onWeekChange: (increment: number) => void;
};

type State = {
    loading: boolean;
    delete_mode: boolean;
    uses_pool: boolean;
    print_only_assigned_lines: boolean;
    competences: t.ILineCompetenceData[];
    lines: t.IShiftPersonLineRec[]; // all lines in TED
    lines_non_pooled: t.IShiftPersonLineRec[]; // non-pooled lines (these ones are displayed in initial screen)
    lines_pooled: t.IShiftPersonLineRec[]; // pooled lines (these ones are NOT displayed in initial screen)
    pool_line: t.IShiftPersonLineRec | null; // pool
    lines_previous_week: t.IShiftPersonLineRec[];
    i: number;
    j: number;
    key: string | null;
    error: string;
    /** Hash for all non-pooled lines (all lines in case of no advanced pool scheduling) */
    hash: string;
    /** Hash for pooled lines only (in case of advanced pool scheduling). Empty when not used. */
    hashPooled: string;
    week: number;
    year: number;
    show_tool_change: boolean;
    show_work_orders_in_print: boolean;
    show_export_day_modal: boolean;
    show_export_week_modal: boolean;
    include_prev_sunday: boolean;
    include_next_sunday: boolean;
    daily_message: string;
    export_day_of_week: number;
    export_day_shift_tag: string;
    unsaved_changes: boolean;
    worker_hours: Map<string, number>;
    show_settings_editor: boolean;
    show_worker_alert: boolean;
    showNX: boolean;
    showA: boolean;
    showC: boolean;
    showS: boolean;
    showR: boolean;
    showPoolEditor: boolean;
    poolEditorShift: number;
    poolEditorShiftTag: string;
    print_whole_week: boolean;
    columns_as_shifts: boolean;
    show_week_pool_editor: boolean;
    shift_tags: string[];
};

let bullet_key_counter = 0;
function createBullet(type: string, text: string, tooltip: string | null, is_wide?: boolean) {
    const css = is_wide ? " badge-wide" : "";
    const key = "bullet" + bullet_key_counter++;
    if (type === "error") {
        return (
            <span key={key} className={"badge badge-pill badge-danger" + css} title={tooltip ?? undefined}>
                {text}
            </span>
        );
    }
    if (type === "warning") {
        return (
            <span key={key} className={"badge badge-pill badge-warn" + css} title={tooltip ?? undefined}>
                {text}
            </span>
        );
    }
    return (
        <span key={key} className={"badge badge-pill badge-info" + css} title={tooltip ?? undefined}>
            {text}
        </span>
    );
}

const defaultToggleValues = {
    showC: false,
    showNX: false,
    showA: false,
    showR: true,
    showS: true,
    showToolChange: true,
    showWorkOrderPrint: true,
};

type ToggleNamesCompetences = "showC" | "showNX" | "showR" | "showS" | "showA";
type ToggleNames = "showC" | "showNX" | "showR" | "showS" | "showA" | "showWorkOrderPrint" | "showToolChange";

const getToggleLocalStorage = (type: ToggleNames): boolean => {
    const value = local_settings.getItem(`shift-assignment-details-${type}`);

    if (value === undefined || value === null) return defaultToggleValues[type];

    return value === "true";
};

const setToggleLocalStorage = (type: ToggleNames, value: boolean) => {
    local_settings.setItem(`shift-assignment-details-${type}`, value.toString());
};

const mapTemplateLines = (template: IPeopleScheduleTemplate | null): TemplateLinesMap => {
    const template_map: TemplateLinesMap = new Map();
    if (template !== null) {
        for (const line of template.lines) {
            template_map.set(
                line.line_uuid,
                line.shifts_people.map(x => new Set(x))
            );
        }
    }

    return template_map;
};

/////////////////////////////////////////
// MAIN COMPONENT

/**
 * Displaying people details and manage competences.
 */
class ShiftAssignmentDetails extends Component<Props, State> {
    /** Last known server-aligned pooled lines; used to skip redundant pooled lines saves. */
    pooledScheduleBaseline: null | t.IShiftPersonLineRec[];

    constructor(props: Props) {
        super(props);

        const state: State = {
            delete_mode: false,
            loading: false,
            uses_pool: false,
            print_only_assigned_lines: false,
            competences: [],
            daily_message: "",
            error: "",
            i: -1,
            j: -1,
            key: null,
            week: this.props.week,
            year: this.props.year,
            hash: "",
            hashPooled: "",
            show_tool_change: getToggleLocalStorage("showToolChange"),
            show_work_orders_in_print: getToggleLocalStorage("showWorkOrderPrint"),
            unsaved_changes: false,
            lines: [],
            lines_non_pooled: [],
            lines_pooled: [],
            pool_line: null,
            lines_previous_week: [],
            show_export_day_modal: false,
            show_export_week_modal: false,
            include_prev_sunday: false,
            include_next_sunday: false,
            export_day_of_week: 0,
            export_day_shift_tag: "",
            worker_hours: new Map(),
            show_settings_editor: false,
            show_worker_alert: false,
            showNX: getToggleLocalStorage("showNX"),
            showA: getToggleLocalStorage("showA"),
            showC: getToggleLocalStorage("showC"),
            showS: getToggleLocalStorage("showS"),
            showR: getToggleLocalStorage("showR"),
            showPoolEditor: false,
            poolEditorShift: -1,
            poolEditorShiftTag: "",
            print_whole_week: false,
            columns_as_shifts: false,
            show_week_pool_editor: false,
            shift_tags: this.getShiftTags(),
        };

        this.state = state;
        this.pooledScheduleBaseline = null;
    }

    /**
     * Refreshes the snapshot of pooled lines used to detect whether `saveChanges` must persist the pooled partition.
     */
    setPooledScheduleBaselineFromLoaded(linesPooled: t.IShiftPersonLineRec[]): void {
        if (lineGroupUsesAdvancedPoolScheduling(this.props.plant, this.props.line_group_uuid)) {
            this.pooledScheduleBaseline = u.deepClone(linesPooled);
        } else {
            this.pooledScheduleBaseline = null;
        }
    }

    /** Whether the advanced-split pooled partition should be persisted in `saveChanges` (non-empty and differs from baseline). */
    pooledPartitionNeedsSave(): boolean {
        const pooledLines = this.state.lines_pooled;
        const baseline = this.pooledScheduleBaseline;
        return pooledLines.length > 0 && (baseline === null || !arePeopleSchedulesEqual(pooledLines, baseline));
    }

    async loadLinesData(line_uuids: string[], year: number, week: number): Promise<t.IGetPeopleShiftsTableRes> {
        const { start_shift_tag, end_shift_tag } = bl.startEndShiftTags(year, week, this.props.plant);
        const data = await getBackend().manufacturing.getPeopleShiftsTable({
            from_shift: start_shift_tag,
            to_shift: end_shift_tag,
            include_tool_changes: true,
            include_work_orders: false,
            line_uuids,
        });
        data.lines = postProcessLines(data.lines);
        return data;
    }

    /**
     * Loads shift table lines separately (in parallel) for non-pooled vs pooled line UUID sets.
     * Each set has its own hash value, allowing to track and save changes to each set separately.
     */
    async loadPeopleShiftsTableParts(
        lineUuidsNonPooled: string[],
        lineUuidsPooled: string[],
        year: number,
        week: number
    ): Promise<{
        lines: t.IShiftPersonLineRec[];
        workerHoursByShift: Map<string, number>;
        nonPooledLinesHash: string;
        pooledLinesHash: string;
    }> {
        const { start_shift_tag, end_shift_tag } = bl.startEndShiftTags(year, week, this.props.plant);
        const reqBase = {
            from_shift: start_shift_tag,
            to_shift: end_shift_tag,
            include_tool_changes: true,
            include_work_orders: false,
        };

        const fetchPeopleSchedule = async (uuids: string[]): Promise<t.IGetPeopleShiftsTableRes> => {
            if (uuids.length === 0) {
                return { lines: [], required_persons: [], hash: "" };
            }

            return getBackend().manufacturing.getPeopleShiftsTable({
                ...reqBase,
                line_uuids: uuids,
            });
        };

        const [resNonPooled, resPooled] = await Promise.all([
            fetchPeopleSchedule(lineUuidsNonPooled),
            fetchPeopleSchedule(lineUuidsPooled),
        ]);

        const lines = postProcessLines([...resNonPooled.lines, ...resPooled.lines]);
        const workerHoursByShift = sumWorkerHoursByShift([
            ...resNonPooled.required_persons,
            ...resPooled.required_persons,
        ]);

        return {
            lines,
            workerHoursByShift,
            nonPooledLinesHash: resNonPooled.hash,
            pooledLinesHash: resPooled.hash,
        };
    }

    async copyPreviousWeek() {
        const line_uuids = this.state.lines.map(x => x.line_uuid);
        // calculate previous week tags
        const year = this.state.year;
        const week = this.state.week;
        const from_date = u.dateFromWeekAndShift(week - 1, year, 0);
        const prev_week = u.shiftNumber(from_date);
        const rotate_direction_linegroup = bl.getLineGroupTagBool(
            this.props.line_group_uuid,
            LINE_GROUP_TAGS.rotate_opposite_direction,
            false
        );
        const copy_strategy_linegroup = bl.getLineGroupTagStr(
            this.props.line_group_uuid,
            LINE_GROUP_TAGS.copy_week_strategy,
            COPY_WEEKLY_ASSIGNMENTS_STRATEGY.shift_preference_rotate // default
        );

        // load data for previous week
        const prev_week_data = await this.loadLinesData(line_uuids, prev_week.year, prev_week.week);

        // update shift tags and week indicators
        prev_week_data.lines.forEach(line => {
            line.shifts.forEach(shift => {
                const from_date = u.dateFromWeekAndShift(week, year, shift.shift_number);
                shift.shift_tag = u.shiftNumber(from_date).shift_tag;
                shift.week = week;
            });
        });

        this.setState(prevState => {
            const week_start_shift_offset = this.getWeekStartShiftOffset();
            for (const line of prevState.lines_non_pooled) {
                const line_last_week = prev_week_data.lines.filter(x => x.line_uuid === line.line_uuid)[0];
                if (!line_last_week) {
                    continue;
                }
                // resolve copy-strategy for line, use line-group setting as fallback
                const copy_strategy_line = (line.line_tags[LINE_TAGS.copy_week_strategy] ||
                    copy_strategy_linegroup) as bl.ShiftCopyingStrategy;
                // the same with rotate_direction
                const rotate_direction_line_tag = line.line_tags[LINE_TAGS.rotate_opposite_direction];
                const rotate_direction_line =
                    rotate_direction_line_tag === "true" ||
                    (rotate_direction_line_tag === undefined && rotate_direction_linegroup);
                bl.copyShiftAssignmentsBetweenWeeks(
                    line_last_week,
                    line,
                    copy_strategy_line,
                    this.props.personAllowedShifts,
                    week_start_shift_offset,
                    rotate_direction_line
                );
            }
            return { lines: prevState.lines, unsaved_changes: true };
        });
    }

    getPlantData(): t.IPlantData | null {
        return bl.getPlantsForUser().find(x => x.uuid === this.props.plant) ?? null;
    }

    getLineGroupData(): t.ILineGroupData | null {
        return bl.getLineGroupForUser(this.props.line_group_uuid);
    }

    getScheduleLines(): t.IShiftPersonLineRec[] {
        return this.state.uses_pool ? this.state.lines_pooled : this.state.lines;
    }

    /** Generates shift tags for the current week, considering the `PLANT_TAGS.week_start_shift_offset` tag */
    getShiftTags(): string[] {
        return Array.from({ length: SHIFTS_IN_WEEK }, (_, i) =>
            bl.shiftTagOffset(this.props.year, this.props.week, i, this.props.plant)
        );
    }

    async componentDidMount() {
        document.addEventListener("keydown", this.keyPressed);
        document.addEventListener("keyup", this.keyUp);

        this.loadData();
    }

    async componentDidUpdate(prev_props: Props) {
        if (
            prev_props.week !== this.props.week ||
            prev_props.plant !== this.props.plant ||
            prev_props.line_group_uuid !== this.props.line_group_uuid
        ) {
            this.loadData();
        }
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this.keyPressed);
        document.removeEventListener("keyup", this.keyUp);
        this.props.onShiftSaveStateChange(false);
    }

    loadData = async () => {
        const shift_tags = this.getShiftTags();
        this.setState({ loading: true, error: "", shift_tags });

        try {
            // beware - not all lines in line-group have shift-board enabled (via tags)
            // so the result from the server might be lacking some lines that are present in this linegroup
            const lgd = this.getLineGroupData();
            if (lgd === null || lgd.line_uuids.length === 0) {
                this.setState({
                    loading: false,
                    error: translate("Manufacturing.Lines.no_lines_in_linegroup", "No lines in line group"),
                });
                return;
            }

            const line_uuids = lgd.line_uuids;
            const year = this.props.year;
            const week = this.props.week;

            let lines: t.IShiftPersonLineRec[];
            let worker_hours: Map<string, number>;
            let hash: string;
            let hashPooled = "";

            if (lineGroupUsesAdvancedPoolScheduling(this.props.plant, this.props.line_group_uuid)) {
                const [pooledLineUuids, nonPooledLineUuids] = partitionLineUuidsByPoolSchedulingRole(
                    line_uuids,
                    this.props.plant
                );
                const split = await this.loadPeopleShiftsTableParts(nonPooledLineUuids, pooledLineUuids, year, week);
                lines = split.lines;
                worker_hours = split.workerHoursByShift;
                hash = split.nonPooledLinesHash;
                hashPooled = split.pooledLinesHash;
            } else {
                const data = await this.loadLinesData(line_uuids, year, week);
                lines = data.lines;
                worker_hours = sumWorkerHoursByShift(data.required_persons);
                hash = data.hash;
            }

            const [lines_pooled, lines_non_pooled] = partitionLinesByPoolSchedulingRole(
                lines,
                this.props.plant,
                this.props.line_group_uuid
            );
            const pool_line = findPoolLine(lines);

            if (lines.length === 0) {
                this.setState({
                    loading: false,
                    error: translate(
                        "ShiftAssignmentDetails.no_lines_enabled_for_shiftboard",
                        "This line-group has no lines enabled for shift-board"
                    ),
                });
                return;
            }

            const uses_pool = LINE_GROUP_TAGS_ACCESS.has_pool(lgd.tags);
            const print_only_assigned_lines = LINE_GROUP_TAGS_ACCESS.print_only_assigned_lines(lgd.tags);

            this.setPooledScheduleBaselineFromLoaded(lines_pooled);
            this.setState(
                {
                    year,
                    week,
                    loading: false,
                    uses_pool,
                    print_only_assigned_lines,
                    lines,
                    lines_non_pooled,
                    lines_pooled,
                    pool_line,
                    hash,
                    hashPooled,
                    worker_hours,
                },
                async () => {
                    this.props.onScheduleUpdate(this.getScheduleLines());
                    try {
                        const data_prev = await this.loadLinesData(line_uuids, year, week - 1);
                        this.setState({
                            lines_previous_week: data_prev.lines,
                        });
                    } catch (error) {
                        console.error("Failed to load previous week schedule:", error);
                    }
                }
            );
        } catch (error) {
            console.error("Failed to load schedule:", error);
            this.setState({
                loading: false,
                error: translate("common.error", "An error occurred while fetching data"),
            });
        }
    };

    hasUnsavedChanges = () => {
        return Auth.roleCanEditShiftBoard() && this.state.unsaved_changes;
    };

    /**
     * Handling any change in checkbox components.
     */
    handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // read new values
        const target = event.currentTarget;
        const name = target.name;

        if (name === "show_tool_change") {
            const show_tool_change = target.checked;
            this.setState({ show_tool_change });
            setToggleLocalStorage("showToolChange", show_tool_change);
        } else if (name === "show_work_orders_in_print") {
            const show_work_orders_in_print = target.checked;
            this.setState({ show_work_orders_in_print });
            setToggleLocalStorage("showWorkOrderPrint", show_work_orders_in_print);
        }
    };

    async exportToHtmlWeekend(): Promise<void> {
        if (this.hasUnsavedChanges()) {
            alert(translate("Shifts.please_save", "The shift schedule has changed. Please save the data."));
            return;
        }

        this.setState({ show_export_week_modal: true, daily_message: "", print_whole_week: false });
    }

    exportToHtmlWeek(params: {
        include_prev_sunday?: boolean;
        include_next_sunday?: boolean;
        columns_as_shifts?: boolean;
    }): void {
        if (this.hasUnsavedChanges()) {
            alert(translate("Shifts.please_save", "The shift schedule has changed. Please save the data."));
            return;
        }
        const { include_prev_sunday = false, include_next_sunday = false, columns_as_shifts = false } = params;
        this.setState({
            show_export_week_modal: true,
            daily_message: "",
            print_whole_week: true,
            include_prev_sunday,
            include_next_sunday,
            columns_as_shifts,
        });
    }

    async startExportToHTMLWeekend(message: string) {
        let lines = this.state.lines_non_pooled;
        this.setState({ show_export_week_modal: false });
        exportWeekToHtml(
            this.state.year,
            this.state.week,
            this.state.include_prev_sunday,
            this.state.include_next_sunday,
            this.state.show_tool_change,
            lines.map(x => x.line_uuid),
            this.props.uniquePersonName,
            this.props.personWorks12Hours,
            message,
            !this.state.print_whole_week,
            this.state.columns_as_shifts
        );
    }

    async startExportToHtmlDay(shift_tag: string): Promise<void> {
        if (this.hasUnsavedChanges()) {
            alert(translate("Shifts.please_save", "The shift schedule has changed. Please save the data."));
            return;
        }
        // load comment from server
        const { year, week, day_number } = u.shiftNumber(u.fromShiftTag(shift_tag));
        const key = `${STORAGE_PREFIX}.${year}.${week}.${day_number}.${this.props.line_group_uuid}`;
        const data = await getBackend().common.getKeyValue({ key });
        let daily_message = "";
        if (data.data) {
            daily_message = data.data.message || "";
        }
        this.setState({
            show_export_day_modal: true,
            export_day_of_week: day_number,
            export_day_shift_tag: shift_tag,
            daily_message,
        });
    }

    async exportToHtmlDay(message: string): Promise<void> {
        this.setState({ error: "" });
        try {
            this.setState({ show_export_day_modal: false });
            const shift_tag = this.state.export_day_shift_tag;

            const { year, week, day_number } = u.shiftNumber(u.fromShiftTag(shift_tag));

            const plant_data = this.getPlantData();
            const mode = bl.getLineGroupTagStr(
                this.props.line_group_uuid,
                LINE_GROUP_TAGS.day_shift_assignment_format,
                "123"
            );
            const show_tool_change = this.state.show_tool_change;
            const show_work_orders = this.state.show_work_orders_in_print;
            // skip pool line for daily export
            const lines = this.state.lines.filter(x => !isPoolModePoolLine(x.line_tags));
            const group_by_people = bl.getLineGroupTagBool(
                this.props.line_group_uuid,
                LINE_GROUP_TAGS.shift_assignment_group_by_people,
                false
            );
            const print_only_assigned_lines = this.state.print_only_assigned_lines;
            await exportDayToHtml(
                year,
                week,
                day_number,
                plant_data?.title || "",
                this.getLineGroupData()?.title || "",
                show_tool_change,
                show_work_orders,
                lines.map(x => x.line_uuid),
                message,
                mode,
                group_by_people,
                print_only_assigned_lines,
                this.props.personWorks12Hours
            );

            // save comment to server
            const key = `${STORAGE_PREFIX}.${year}.${week}.${day_number}.${this.props.line_group_uuid}`;
            const data = { message };
            await getBackend().common.setKeyValue({ key, data });
            this.setState({ daily_message: message });
        } catch (err) {
            this.setState({ error: (err as Error).message });
        }
    }

    async saveChanges() {
        const { onShiftSaveStateChange } = this.props;
        const { start_shift_tag, end_shift_tag } = bl.startEndShiftTags(
            this.state.year,
            this.state.week,
            this.props.plant
        );
        const first_shift_ts = u.shiftNumber(u.fromShiftTag(start_shift_tag)).ts_start;
        const last_shift_ts = u.shiftNumber(u.fromShiftTag(end_shift_tag)).ts_end;

        onShiftSaveStateChange(true);
        this.setState({ error: "" });

        const useSplitPeopleShiftsTable = lineGroupUsesAdvancedPoolScheduling(
            this.props.plant,
            this.props.line_group_uuid
        );
        let nextHash = this.state.hash;
        let nextHashPooled = this.state.hashPooled;

        try {
            if (useSplitPeopleShiftsTable) {
                const nonPooledLines = this.state.lines_non_pooled;
                const pooledLines = this.state.lines_pooled;

                if (nonPooledLines.length > 0) {
                    const resNonPooled = await getBackend().manufacturing.storePeopleShiftAssignments({
                        hash: nextHash,
                        lines: mapShiftPersonLinesToStoreRecs(nonPooledLines),
                        first_shift_ts,
                        last_shift_ts,
                    });
                    nextHash = resNonPooled.hash;
                }

                if (this.pooledPartitionNeedsSave()) {
                    const resPooled = await getBackend().manufacturing.storePeopleShiftAssignments({
                        hash: nextHashPooled,
                        lines: mapShiftPersonLinesToStoreRecs(pooledLines),
                        first_shift_ts,
                        last_shift_ts,
                    });
                    nextHashPooled = resPooled.hash;
                    this.pooledScheduleBaseline = u.deepClone(pooledLines);
                }

                this.setState({ hash: nextHash, hashPooled: nextHashPooled, unsaved_changes: false });
            } else {
                const req: t.IStorePeopleShiftAssignmentsReq = {
                    hash: this.state.hash,
                    lines: mapShiftPersonLinesToStoreRecs(this.state.lines),
                    first_shift_ts,
                    last_shift_ts,
                };
                const res = await getBackend().manufacturing.storePeopleShiftAssignments(req);
                nextHash = res.hash;
                this.setState({ hash: nextHash, unsaved_changes: false });
            }
            this.props.onShiftsSaved();
        } catch (err) {
            console.error("Failed to save shift assignments:", err);
            const errorMessage = translate("common.error_save", "An error occurred while saving data");
            if (useSplitPeopleShiftsTable) {
                this.setState({ error: errorMessage, hash: nextHash, hashPooled: nextHashPooled });
            } else {
                this.setState({ error: errorMessage });
            }
        } finally {
            onShiftSaveStateChange(false);
        }
    }

    mouseOver(i: number, j: number): void {
        if (this.state.i !== i || this.state.j !== j) {
            this.setState({ i, j });
        }
    }

    clearShiftSchedule(i: number, j: number): void {
        this.setState(
            prevState => {
                const lines = this.state.uses_pool ? prevState.lines_non_pooled : prevState.lines;
                const line_tags = lines[i].line_tags;
                if (this.state.uses_pool && isPoolModePoolLine(line_tags)) {
                    lines[i].shifts[j].persons = [];
                    const lines_pooled = prevState.lines_pooled;
                    for (const line of lines_pooled) {
                        line.shifts[j].persons = [];
                    }
                    return { lines, lines_pooled, unsaved_changes: true } as State;
                } else {
                    lines[i].shifts[j].persons = [];
                    return { lines, unsaved_changes: true };
                }
            },
            () => {
                this.props.onScheduleUpdate(this.getScheduleLines());
            }
        );
    }

    mouseDownSchedule(i: number, j: number): void {
        if (this.state.delete_mode) {
            this.clearShiftSchedule(i, j);
            return;
        }
        if (this.props.selectedPerson === null) {
            return;
        }
        const person_id = this.props.selectedPerson.id;
        const person_uuid = this.props.selectedPerson.uuid;
        const person_name = this.props.selectedPerson.name;
        let show_worker_alert = false;
        const personAddToOrRemoveFromList = (shift: t.IShiftPersonLineRecItem, line_tags: t.ITags) => {
            if (shift.persons.some(x => x.uuid === person_uuid)) {
                // show alert if user wants to remove person from the pool and is already asigned to lines
                if (this.state.uses_pool && isPoolModePoolLine(line_tags)) {
                    const assigned_workers: Set<string>[] = this.findAssignedWorkers();
                    if (assigned_workers[shift.shift_number].has(person_uuid)) {
                        show_worker_alert = true;
                        return;
                    }
                }
                // remove person from list
                shift.persons = shift.persons.filter(x => x.uuid !== person_uuid);
            } else {
                // add person to list
                shift.persons.push({ name: person_name, uuid: person_uuid, id: person_id });
                shift.persons = shift.persons.sort((a, b) => u.stringCompare(a.name, b.name));
            }
        };
        this.setState(
            prevState => {
                const lines = this.state.uses_pool ? prevState.lines_non_pooled : prevState.lines;
                const line_tags = lines[i].line_tags;
                if (prevState.key === "Control") {
                    j = j % 3;
                    for (let k = 0; k < 5; k++) {
                        const shift = lines[i].shifts[3 * k + j];
                        personAddToOrRemoveFromList(shift, line_tags);
                    }
                } else {
                    const shift = lines[i].shifts[j];
                    personAddToOrRemoveFromList(shift, line_tags);
                }
                return this.state.uses_pool
                    ? ({ lines_non_pooled: lines, unsaved_changes: true, show_worker_alert } as State)
                    : { lines, unsaved_changes: true };
            },
            () => {
                this.props.onScheduleUpdate(this.getScheduleLines());
            }
        );
    }

    applyShiftTemplate(): boolean {
        const { template } = this.props;
        if (template === null) {
            return false;
        }

        const { lines } = this.state;
        const people_map: Map<string, t.IPersonData> = new Map(this.props.people.map(x => [x.uuid, x]));
        const lines_map: Map<string, t.IShiftPersonLineRec> = new Map(lines.map(x => [x.line_uuid, x]));

        for (const template_line of template.lines) {
            const line = lines_map.get(template_line.line_uuid);
            if (line === undefined) {
                continue;
            }

            for (let i = 0; i < template_line.shifts_people.length; i++) {
                const shift_people = template_line.shifts_people[i];
                const shift = line.shifts[i];
                if (shift === undefined) {
                    continue;
                }

                const shift_persons_map: Map<string, t.IShiftPersonLineRecPerson> = new Map(
                    shift.persons.map(x => [x.uuid, x])
                );
                for (const person_uuid of shift_people) {
                    const shift_person = shift_persons_map.get(person_uuid);
                    if (shift_person === undefined) {
                        const person = people_map.get(person_uuid);
                        shift.persons.push({
                            uuid: person_uuid,
                            name: person !== undefined ? person.name : "",
                            id: person !== undefined ? person.id : 0,
                        });
                    }
                }
            }
        }

        this.setState({ lines });
        return true;
    }

    clearShiftTemplate(i: number, j: number): void {
        const template: t.IPeopleScheduleTemplate | null = u.deepClone(this.props.template);
        if (template === null) {
            return;
        }

        const lines = this.state.uses_pool ? this.state.lines_pooled : this.state.lines_non_pooled;
        const line = lines[i];
        if (line === undefined) {
            return;
        }

        let template_line = template.lines.find(l => l.line_uuid === line.line_uuid);
        if (template_line === undefined) {
            return;
        }

        if (template_line.shifts_people.length <= j) {
            return;
        }

        template_line.shifts_people[j] = [];
        this.props.onTemplateChange(template);
    }

    mouseDownTemplate(i: number, j: number): void {
        if (this.state.delete_mode) {
            this.clearShiftTemplate(i, j);
            return;
        }

        if (this.props.selectedPerson === null) {
            return;
        }

        const person_uuid = this.props.selectedPerson.uuid;
        const template: t.IPeopleScheduleTemplate | null = u.deepClone(this.props.template);
        if (template === null) {
            return;
        }

        const lines = this.state.uses_pool ? this.state.lines_pooled : this.state.lines_non_pooled;
        const line = lines[i];
        if (line === undefined) {
            return;
        }

        let template_line = template.lines.find(l => l.line_uuid === line.line_uuid);
        if (template_line === undefined) {
            template_line = {
                line_uuid: line.line_uuid,
                shifts_people: [],
            };
            template.lines.push(template_line);
        }

        const m = j % 3;
        const shift_indices = this.state.key === "Control" ? [0, 1, 2, 3, 4].map(x => 3 * x + m) : [j];
        while (template_line.shifts_people.length < shift_indices[shift_indices.length - 1] + 1) {
            template_line.shifts_people.push([]);
        }

        for (const shift_index of shift_indices) {
            const people_uuids = template_line.shifts_people[shift_index];
            const uuid_index = people_uuids.indexOf(person_uuid);
            if (uuid_index < 0) {
                people_uuids.push(person_uuid);
            } else {
                people_uuids.splice(uuid_index, 1);
            }
        }

        let cut_off_index = template_line.shifts_people.length - 1;
        while (cut_off_index >= 0 && template_line.shifts_people[cut_off_index].length === 0) {
            cut_off_index--;
        }

        template_line.shifts_people = template_line.shifts_people.slice(0, cut_off_index + 1);

        const template_lines: t.IPeopleScheduleTemplateLine[] = [];
        for (const current_template_line of template.lines) {
            if (current_template_line.shifts_people.length > 0) {
                template_lines.push(current_template_line);
            }
        }

        template.lines = template_lines;
        this.props.onTemplateChange(template);
    }

    keyPressed = (e: KeyboardEvent): void => {
        this.setState(prevState => {
            // if modal is shown, exit
            if (prevState.show_export_day_modal === true) return;

            // initial positions
            let j = prevState.j;
            let i = prevState.i;
            if (i === -1 && j === -1) {
                return;
            }
            const lines = prevState.lines;
            if (prevState.key !== e.key) {
                if (e.key === "ArrowRight") {
                    j++;
                    if (j > 20) {
                        j = 20;
                    }
                } else if (e.key === "ArrowLeft") {
                    j--;
                    if (j < 0) {
                        j = 0;
                    }
                } else if (e.key === "ArrowDown") {
                    i++;
                    if (i > lines.length - 1) {
                        i = lines.length - 1;
                    }
                } else if (e.key === "ArrowUp") {
                    i--;
                    if (i < 0) {
                        i = 0;
                    }
                } else if (e.key === "Enter") {
                    if (this.props.template_mode) {
                        this.mouseDownTemplate(i, j);
                    } else {
                        this.mouseDownSchedule(i, j);
                    }
                } else if (e.key === "Delete") {
                    if (!this.props.template_mode) {
                        lines[i].shifts[j].persons = [];
                    }
                }

                return { i, j, key: e.key, lines } as State;
            }
        });
    };

    keyUp = (): void => {
        if (this.state.key !== null) {
            this.setState({ key: null });
        }
    };

    handleClickRow(i: number): void {
        this.setState(prevState => {
            const line = prevState.lines[i];
            line.shifts.forEach(shift => {
                shift.enabled = !shift.enabled;
            });
            return { i, lines: prevState.lines, unsaved_changes: true };
        });
    }

    handlePersonMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
        const target = event.currentTarget;
        if (event.altKey) {
            target.classList.add("hover");
        }
    };

    handlePersonMouseLeave = (event: React.MouseEvent<HTMLDivElement>) => {
        const target = event.currentTarget;
        target.classList.remove("hover");
    };

    handlePersonMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
        const target = event.currentTarget;
        if (event.altKey) {
            target.classList.add("hover");
        } else {
            target.classList.remove("hover");
        }
    };

    handlePersonClick = (event: React.MouseEvent<HTMLDivElement>, person_uuid: string) => {
        if (event.altKey) {
            event.stopPropagation();
            this.props.onPersonClick(person_uuid);
            const person_element = document.getElementById(`person-list-person-${person_uuid}`);
            if (person_element) {
                person_element.scrollIntoView();
            }
        }
    };

    handleWeekPoolScheduleClick = () => {
        this.setState({
            show_week_pool_editor: true,
            poolEditorShiftTag: "",
        });
    };

    handleSchedulePool = (shift: number, shift_tag: string) => {
        if (lineGroupUsesAdvancedPoolScheduling(this.props.plant, this.props.line_group_uuid)) {
            this.setState({
                show_week_pool_editor: true,
                poolEditorShiftTag: shift_tag,
            });
            return;
        }

        this.setState({ showPoolEditor: true, poolEditorShift: shift, poolEditorShiftTag: shift_tag });
    };

    // if line is type tool_setup, check if person is competent adjuster
    isPersonCompetentAdjuster(line: t.IShiftPersonLineRec, person: t.IShiftPersonLineRecPerson): boolean {
        if (line.line_tags[LINE_TAGS.type] === LINE_TYPES.tool_setup) {
            return this.props.isPersonAdjuster(person.uuid);
        } else {
            return false;
        }
    }

    isLoading(): boolean {
        return this.state.loading || this.props.isLoading;
    }

    generateShiftTable(week: number, year: number, lines: t.IShiftPersonLineRec[], worker_hours: Map<string, number>) {
        const day_strings = this.state.shift_tags.map(x => {
            const date = u.fromShiftTag(x);
            const dayn = positiveModulo(date.getDay() - 1, 7);
            return u.dayString(dayn);
        });

        // startDate is the date corresponding to the first shift (could be any day)
        let startDate = u.fromShiftTag(this.state.shift_tags[0]);

        const daysRow: React.ReactNode[] = [];
        let shiftIndex = 0;
        while (shiftIndex < day_strings.length) {
            const currentDay = day_strings[shiftIndex];
            // Count how many times the same day repeats consecutively.
            let count = 1;
            while (shiftIndex + count < day_strings.length && day_strings[shiftIndex + count] === currentDay) {
                count++;
            }

            // Create a date string based on the current startDate.
            const dateStr = startDate.getDate() + ". " + (startDate.getMonth() + 1) + ".";
            const shift_tag = this.state.shift_tags[shiftIndex];

            daysRow.push(
                <th
                    key={shiftIndex}
                    className={u.classNames(
                        "th-wrapper shift-table-days text-center width20px cursor-pointer date td-right-border",
                        {
                            "shift-table-header-sunday": currentDay === u.dayString(6),
                            "shift-table-header-saturday": currentDay === u.dayString(5),
                        }
                    )}
                    colSpan={count}
                    scope="col"
                    onClick={() => this.startExportToHtmlDay(shift_tag)}
                >
                    {currentDay} - {dateStr}
                </th>
            );

            // Move to the next group.
            shiftIndex += count;
            // Advance startDate by one day
            startDate = new Date(startDate.getTime() + 1 * u.TIME_RANGES.DAY);
        }

        let shifts = this.state.shift_tags.map(x => {
            const shift_number = parseInt(x.slice(6));
            return shift_number;
        });

        const shifts_header = shifts.map((shift, i) => {
            let types = ["1", "2", "3"];
            let type = types[shift % 3];
            return (
                <th
                    key={shift + i}
                    className={u.classNames("th-wrapper shift-table-days shift text-center width20px", {
                        "td-right-border": shift % 3 === 2,
                    })}
                >
                    {type}
                </th>
            );
        });

        const needed_workers_row = this.state.shift_tags.map((shift_tag, i) => {
            const shift = u.shiftNumber(u.fromShiftTag(shift_tag)).shift;
            let hours = worker_hours.get(shift_tag);
            if (hours === undefined) {
                hours = 0;
            }

            const value = worker_hours.has(shift_tag) ? niceNumber(Math.ceil(hours / 8), 0) : "-";
            return (
                <th
                    key={"required_worker_" + shift + "_" + i}
                    className={u.classNames("th-wrapper shift-table-days required_workers text-center width20px", {
                        "td-right-border": shift % 3 === 2,
                    })}
                >
                    {value}
                </th>
            );
        });

        // for each day+person combination count the number of shifts and lines
        // if person is assigned to more than 1 shift per day, draw special indicator
        const counter_person_day: Map<string, number> = new Map();
        const absent_worker_assigned: Map<string, boolean> = new Map();
        const tagForDay = (shift: t.IShiftPersonLineRecItem) =>
            shift.shift_tag.slice(0, 6) + Math.floor(shift.shift_number / 3);
        //  count how many times person has been assigned to a shift in a day
        for (const line of lines) {
            for (const shift of line.shifts) {
                const day_tag = tagForDay(shift);
                for (const person of shift.persons) {
                    const key = `${person.uuid}.${day_tag}`;

                    // count person shift per day
                    if (!counter_person_day.has(key)) {
                        counter_person_day.set(key, 0);
                    }
                    counter_person_day.set(key, (counter_person_day.get(key) || 0) + 1);
                }
            }
        }

        // indicate that worker is assigned to absent line and some other line as well
        for (const line of lines) {
            // check if line is absent type (sick_leave, non_work)
            const is_absent_line_type =
                LINE_TAGS_ACCESS.type(line.line_tags) === LINE_TYPES.sick_leave ||
                LINE_TAGS_ACCESS.type(line.line_tags) === LINE_TYPES.non_work;

            // check only for lines witch represent absent worker
            if (is_absent_line_type) {
                for (const shift of line.shifts) {
                    const day_tag = tagForDay(shift);
                    for (const person of shift.persons) {
                        const key = `${person.uuid}.${day_tag}`;
                        // temperary counter_person_day variable,
                        // (because undefined cannot be compared with number)
                        const temp_counter_person_day = counter_person_day.get(key);
                        // indicate absent worker
                        if (temp_counter_person_day != undefined && temp_counter_person_day > 1) {
                            absent_worker_assigned.set(key, true);
                        }
                    }
                }
            }
        }

        const overworked_people: Set<string> = this.findOverworkedIndicators();
        const assigned_workers: Set<string>[] = this.findAssignedWorkers();
        const order_text = translate("common.order", "Order");
        const change_to_text = translate("ShiftDetails.change_to", "Change to");

        const show_required_persons_indicators =
            bl.getLineGroupTagStr(
                this.props.line_group_uuid,
                LINE_GROUP_TAGS.planning_mode,
                PLANNING_MODE.lines_first
            ) === PLANNING_MODE.lines_first;
        let has_pool_line = false;
        let lines_table = lines.map((line, i) => {
            const line_is_pool = isPoolModePoolLine(line.line_tags);
            has_pool_line = has_pool_line || line_is_pool;
            const line_is_ordinary = isPoolModeSchedulingLine(line.line_tags, this.props.plant);
            let shifts_row = shifts.map((shift, j) => {
                const shift_data = line.shifts[j];
                let class_td = "colorEmpty";
                let title_td = "";
                if (i === this.state.i && j === this.state.j) {
                    class_td = "shift-selected";
                }

                if (this.state.key === "Control") {
                    if (i === this.state.i && j % 3 === this.state.j % 3 && j < 15) {
                        class_td = "shift-selected";
                    }
                }

                if (shift_data.persons.length > 0) {
                    class_td += " shift-person";
                }

                const shift_status_class = getShiftCellStatusCssClass(shift_data, line_is_ordinary);
                if (shift_status_class !== "") {
                    class_td += ` ${shift_status_class}`;
                }

                if (this.state.show_tool_change && shift_data.tool_changes.length > 0) {
                    class_td += " tool_setup";
                    title_td += shift_data.tool_changes
                        .map(
                            i =>
                                `${change_to_text}: ${i.material_ext_id}, ${i.title} (${order_text} ${i.order_external_id})`
                        )
                        .join("\n");
                }

                class_td += " text-center td-shift-name td-wrapper";
                let persons = shift_data.persons
                    .map(person => {
                        let class_td = "person";
                        const day_tag = tagForDay(shift_data);
                        const key_person_shift = `${person.uuid}.${shift_data.shift_tag}.${i}`;
                        const key_person_day = `${person.uuid}.${day_tag}`;
                        let indicators: React.ReactNode[] = [];
                        const day_shifts_for_this_person = counter_person_day.get(key_person_day) || 0;
                        if (day_shifts_for_this_person > 1 && this.state.showNX) {
                            indicators.push(
                                createBullet(
                                    "warning",
                                    day_shifts_for_this_person + "x",
                                    translate(
                                        "ShiftDetails.IconText.shiftNx",
                                        "Assigned to multiple lines within the same day"
                                    )
                                )
                            );
                        }
                        if (
                            !this.props.canPersonWorkInShift(person.uuid, shift_data.shift_number % 3) &&
                            this.state.showS
                        ) {
                            indicators.push(
                                createBullet(
                                    "error",
                                    translate("ShiftDetails.Icon.shift", "S"),
                                    translate("ShiftDetails.IconText.shift", "Invalid shift")
                                )
                            );
                        }
                        if (overworked_people.has(person.uuid) && this.state.showR) {
                            indicators.push(
                                createBullet(
                                    "error",
                                    translate("ShiftDetails.Icon.reduced_workshift", "R"),
                                    translate("ShiftDetails.IconText.reduced_workshift", "Assigned to too many shifts")
                                )
                            );
                        }

                        // unassigned workers in pool
                        if (line_is_pool && !assigned_workers[shift].has(person.uuid)) {
                            class_td += " pool-edit-person-not-assigned";
                        }

                        // worker assigned to sick_line and some other as well
                        if (absent_worker_assigned.get(key_person_day)) {
                            indicators.push(
                                createBullet(
                                    "warning",
                                    translate("ShiftDetails.Icon.absent_worker_assigned", "A"),
                                    translate("ShiftDetails.IconText.absent_worker_assigned", "Absent worker assigned")
                                )
                            );
                        }

                        if (
                            !this.props.canPersonWorkOnLine(person.uuid, line.line_uuid) &&
                            !IGNORE_COMPETENCE_FLAG.includes(line.line_tags[LINE_TAGS.type] as LineTypesEnum) &&
                            !this.isPersonCompetentAdjuster(line, person) &&
                            this.state.showC
                        ) {
                            indicators.push(
                                createBullet(
                                    "info",
                                    translate("ShiftDetails.Icon.competence", "C"),
                                    translate("ShiftDetails.IconText.competence", "Unqualified for the line")
                                )
                            );
                        }
                        // check if person works 12h shifts
                        if (this.props.personWorks12Hours(person.uuid)) {
                            class_td += " person-12h-shift";
                        }
                        return {
                            class_td,
                            key: key_person_shift,
                            uuid: person.uuid,
                            indicators,
                            name: this.props.uniquePersonName(person.uuid),
                        };
                    })
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(x => (
                        <div
                            key={x.key}
                            className={x.class_td}
                            onMouseEnter={this.handlePersonMouseEnter}
                            onMouseLeave={this.handlePersonMouseLeave}
                            onMouseMove={this.handlePersonMouseMove}
                            onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                this.handlePersonClick(event, x.uuid);
                            }}
                        >
                            {x.indicators} {x.name}
                        </div>
                    ));

                const person_uuid = this.props.selectedPerson ? this.props.selectedPerson.uuid : null;
                if (person_uuid && shift_data.persons.some(x => x.uuid === person_uuid)) {
                    class_td += " shift-person-selected";
                }

                return (
                    <td
                        key={j}
                        onMouseOver={() => this.mouseOver(i, j)}
                        onClick={() => this.mouseDownSchedule(i, j)}
                        className={class_td}
                        title={title_td}
                    >
                        <div>{persons}</div>
                    </td>
                );
            });

            const line_title_css = getLineTitleCssClass(line.line_tags);

            return (
                <tr key={i}>
                    <td
                        className="td-wrapper width20px text-center"
                        onMouseDown={() => {
                            this.handleClickRow(i);
                        }}
                    >
                        <div className={line_title_css}>{line.line_title}</div>
                    </td>
                    {shifts_row}
                </tr>
            );
        });

        // in case we have a pool, add extra line at the bottom
        if (has_pool_line && show_required_persons_indicators) {
            // prepare shift statistics
            const shift_stats: ShiftStatistics[] = this.prepareShiftStatistics();
            lines_table.push(
                <tr key={lines_table.length} className="pool-extra-line" data-testid="pool-extra-line">
                    <td className="td-wrapper width20px text-center">
                        {lineGroupUsesAdvancedPoolScheduling(this.props.plant, this.props.line_group_uuid) && (
                            <button className="btn btn-secondary" onClick={this.handleWeekPoolScheduleClick}>
                                <FormattedMessage id="ShiftDetails.schedule" defaultMessage="Schedule" />
                            </button>
                        )}
                    </td>
                    {shifts.map((shift, i) => (
                        <td key={shift} className="colorEmpty">
                            <button
                                className="btn btn-secondary"
                                onClick={() => this.handleSchedulePool(shift, this.state.shift_tags[i])}
                            >
                                <FormattedMessage id="ShiftDetails.schedule" defaultMessage="Schedule" />
                                <br />
                                {shift_stats[shift].scheduled_lines} / {shift_stats[shift].active_lines}
                            </button>
                        </td>
                    ))}
                </tr>
            );
        }

        return (
            <table className="table table-shifts table-bordered" id="table-shifts">
                <thead id="table-shifts-header">
                    <tr>
                        <th scope="col" className="th-wrapper shift-table-days date text-center width20px">
                            {translate("common.day", "Day")}
                        </th>
                        {daysRow}
                    </tr>
                    <tr>
                        <th scope="col" className="th-wrapper shift-table-days shift text-center width20px">
                            {translate("common.shift", "Shift")}
                        </th>
                        {shifts_header}
                    </tr>
                    {show_required_persons_indicators && (
                        <tr>
                            <th
                                scope="col"
                                className="th-wrapper shift-table-days required_workers text-center width20px"
                            >
                                {translate("Shifts.estimated_number_of_people", "Predvideno potrebno število ljudi")}
                            </th>
                            {needed_workers_row}
                        </tr>
                    )}
                </thead>
                <tbody>{lines_table}</tbody>
            </table>
        );
    }

    // TODO: Split this function into smaller ones and reuse them in generateScheduleShiftTable
    generateTemplateShiftTable(
        week: number,
        year: number,
        lines: t.IShiftPersonLineRec[],
        worker_hours: Map<string, number>,
        template: IPeopleScheduleTemplate | null
    ) {
        let monday = u.getDateOfISOWeek(week, year);
        const days_row = u.dayStrings().map((day, i) => {
            // add a day in the next line
            const date = monday.getDate() + ". " + (monday.getMonth() + 1) + ".";
            // add a day
            monday = new Date(monday.getTime() + 1 * u.TIME_RANGES.DAY);

            return (
                <th
                    key={i}
                    className={u.classNames(
                        "th-wrapper shift-table-days text-center width20px cursor-pointer date td-right-border",
                        {
                            "shift-table-header-sunday": i === 6,
                            "shift-table-header-saturday": i === 5,
                        }
                    )}
                    colSpan={3}
                    scope="col"
                >
                    {day} - {date}
                </th>
            );
        });

        const shifts = [...Array(21).keys()];
        const shifts_header = shifts.map((shift, i) => {
            let types = ["1", "2", "3"];
            let type = types[i % 3];
            return (
                <th
                    key={i}
                    className={u.classNames("th-wrapper shift-table-days shift text-center width20px", {
                        "td-right-border": i % 3 === 2,
                    })}
                >
                    {type}
                </th>
            );
        });

        const needed_workers_row = shifts.map((shift, i) => {
            const shift_tag = shiftTag(year, week, shift);
            let hours = worker_hours.get(shift_tag);
            if (hours === undefined) {
                hours = 0;
            }

            const value = worker_hours.has(shift_tag) ? niceNumber(Math.ceil(hours / 8), 0) : "-";
            return (
                <th
                    key={"required_worker_" + shift + "_" + i}
                    className={u.classNames("th-wrapper shift-table-days required_workers text-center width20px", {
                        "td-right-border": i % 3 === 2,
                    })}
                >
                    {value}
                </th>
            );
        });

        const people_map: Map<string, t.IPersonData> = new Map(this.props.people.map(x => [x.uuid, x]));
        const template_lines_map: TemplateLinesMap = mapTemplateLines(template);

        // for each day+person combination count the number of shifts and lines
        // if person is assigned to more than 1 shift per day, draw special indicator
        const counter_person_day: Map<string, number> = new Map();
        for (const line of [...template_lines_map.values()]) {
            for (let i = 0; i < line.length; i++) {
                const shift = line[i];
                const day_index = Math.floor(i / 3);
                for (const person_uuid of [...shift]) {
                    const key = `${person_uuid}.${day_index}`;
                    if (!counter_person_day.has(key)) {
                        counter_person_day.set(key, 0);
                    }

                    counter_person_day.set(key, (counter_person_day.get(key) || 0) + 1);
                }
            }
        }

        const order_text = translate("common.order", "Order");
        const change_to_text = translate("ShiftDetails.change_to", "Change to");

        const show_required_persons_indicators =
            bl.getLineGroupTagStr(
                this.props.line_group_uuid,
                LINE_GROUP_TAGS.planning_mode,
                PLANNING_MODE.lines_first
            ) === PLANNING_MODE.lines_first;
        let has_pool_line = false;
        let lines_table = lines.map((line, i) => {
            const line_is_pool = isPoolModePoolLine(line.line_tags);
            has_pool_line = has_pool_line || line_is_pool;
            const line_is_ordinary = isPoolModeSchedulingLine(line.line_tags, this.props.plant);
            let shifts_row = shifts.map((shift, j) => {
                const shift_data = line.shifts[j];
                let class_td = "colorEmpty";
                let title_td = "";
                if (i === this.state.i && j === this.state.j) {
                    class_td = "shift-selected";
                }

                if (this.state.key === "Control") {
                    if (i === this.state.i && j % 3 === this.state.j % 3 && j < 15) {
                        class_td = "shift-selected";
                    }
                }

                if (shift_data.persons.length > 0) {
                    class_td += " shift-person";
                }

                const shift_status_class = getShiftCellStatusCssClass(shift_data, line_is_ordinary);
                if (shift_status_class !== "") {
                    class_td += ` ${shift_status_class}`;
                }

                if (this.state.show_tool_change && shift_data.tool_changes.length > 0) {
                    class_td += " tool_setup";
                    title_td += shift_data.tool_changes
                        .map(
                            i =>
                                `${change_to_text}: ${i.material_ext_id}, ${i.title} (${order_text} ${i.order_external_id})`
                        )
                        .join("\n");
                }

                class_td += " text-center td-shift-name td-wrapper";

                let shift_people_uuids: string[] = [];
                const line_shifts = template_lines_map.get(line.line_uuid);
                if (line_shifts !== undefined && line_shifts[j] !== undefined) {
                    shift_people_uuids = [...line_shifts[j]];
                }

                const persons_data: {
                    name: string;
                    uuid: string;
                    class_name: string;
                    indicators: React.ReactNode[];
                }[] = [];

                for (const person_uuid of shift_people_uuids) {
                    const person = people_map.get(person_uuid);
                    if (person === undefined) {
                        continue;
                    }

                    let class_td_div = "person";
                    const day_index = Math.floor(j / 3);
                    const key_person_day = `${person.uuid}.${day_index}`;
                    let indicators: React.ReactNode[] = [];
                    const person_day_shifts_num = counter_person_day.get(key_person_day) || 0;
                    if (person_day_shifts_num > 1 && this.state.showNX) {
                        indicators.push(
                            createBullet(
                                "warning",
                                person_day_shifts_num + "x",
                                translate(
                                    "ShiftDetails.IconText.shiftNx",
                                    "Assigned to multiple lines within the same day"
                                )
                            )
                        );
                    }
                    if (
                        !this.props.canPersonWorkInShift(person.uuid, shift_data.shift_number % 3) &&
                        this.state.showS
                    ) {
                        indicators.push(
                            createBullet(
                                "error",
                                translate("ShiftDetails.Icon.shift", "S"),
                                translate("ShiftDetails.IconText.shift", "Invalid shift")
                            )
                        );
                    }

                    if (
                        !this.props.canPersonWorkOnLine(person.uuid, line.line_uuid) &&
                        !IGNORE_COMPETENCE_FLAG.includes(line.line_tags[LINE_TAGS.type] as LineTypesEnum) &&
                        !this.isPersonCompetentAdjuster(line, {
                            uuid: person.uuid,
                            name: person.name,
                            id: person.id,
                        }) &&
                        this.state.showC
                    ) {
                        indicators.push(
                            createBullet(
                                "info",
                                translate("ShiftDetails.Icon.competence", "C"),
                                translate("ShiftDetails.IconText.competence", "Unqualified for the line")
                            )
                        );
                    }
                    // check if person works 12h shifts
                    if (this.props.personWorks12Hours(person.uuid)) {
                        class_td_div += " person-12h-shift";
                    }

                    persons_data.push({
                        name: this.props.uniquePersonName(person.uuid),
                        uuid: person.uuid,
                        class_name: class_td_div,
                        indicators,
                    });
                }

                persons_data.sort((a, b) => a.name.localeCompare(b.name));
                const persons = persons_data.map(x => (
                    <div
                        key={x.uuid}
                        className={x.class_name}
                        onMouseEnter={this.handlePersonMouseEnter}
                        onMouseLeave={this.handlePersonMouseLeave}
                        onMouseMove={this.handlePersonMouseMove}
                        onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                            this.handlePersonClick(event, x.uuid);
                        }}
                    >
                        {x.indicators} {x.name}
                    </div>
                ));
                const person_uuid = this.props.selectedPerson ? this.props.selectedPerson.uuid : null;
                if (person_uuid && shift_people_uuids.some(uuid => uuid === person_uuid)) {
                    class_td += " shift-person-selected";
                }

                return (
                    <td
                        key={j}
                        onMouseOver={() => this.mouseOver(i, j)}
                        onClick={() => this.mouseDownTemplate(i, j)}
                        className={class_td}
                        title={title_td}
                    >
                        <div>{persons}</div>
                    </td>
                );
            });

            const line_title_css = getLineTitleCssClass(line.line_tags);

            return (
                <tr key={i}>
                    <td className="td-wrapper width20px text-center">
                        <div className={line_title_css}>{line.line_title}</div>
                    </td>
                    {shifts_row}
                </tr>
            );
        });

        return (
            <table className="table table-shifts table-bordered" id="table-shifts">
                <thead>
                    <tr>
                        <th scope="col" className="th-wrapper shift-table-days date text-center width20px">
                            {translate("common.day", "Day")}
                        </th>
                        {days_row}
                    </tr>
                    <tr>
                        <th scope="col" className="th-wrapper shift-table-days shift text-center width20px">
                            {translate("common.shift", "Shift")}
                        </th>
                        {shifts_header}
                    </tr>
                    {show_required_persons_indicators && (
                        <tr>
                            <th
                                scope="col"
                                className="th-wrapper shift-table-days required_workers text-center width20px"
                            >
                                {translate("Shifts.estimated_number_of_people", "Predvideno potrebno število ljudi")}
                            </th>
                            {needed_workers_row}
                        </tr>
                    )}
                </thead>
                <tbody>{lines_table}</tbody>
            </table>
        );
    }

    // For each shift find assigned workers
    findAssignedWorkers(): Set<string>[] {
        const assigned_workers: Set<string>[] = [];
        for (let s = 0; s < 21; s++) {
            assigned_workers.push(new Set());
        }
        for (const line of this.state.lines) {
            if (isPoolModeSchedulingLine(line.line_tags, this.props.plant)) {
                for (const shift of line.shifts) {
                    for (const person of shift.persons) {
                        assigned_workers[shift.shift_number].add(person.uuid);
                    }
                }
            }
        }
        return assigned_workers;
    }

    // For each shift count the number of active lines and scheduled lines
    prepareShiftStatistics(): ShiftStatistics[] {
        const shift_stats: ShiftStatistics[] = [];
        for (let s = 0; s < 21; s++) {
            shift_stats.push({
                active_lines: 0,
                scheduled_lines: 0,
            });
        }
        for (const line of this.state.lines) {
            if (isPoolModeSchedulingLine(line.line_tags, this.props.plant)) {
                for (const shift of line.shifts) {
                    if (shift.enabled) {
                        shift_stats[shift.shift_number].active_lines += 1;
                        if (shift.persons.length > 0) {
                            shift_stats[shift.shift_number].scheduled_lines += 1;
                        }
                    }
                }
            }
        }
        return shift_stats;
    }

    /** Extracts week-start-shift offset from plant's tags */
    getWeekStartShiftOffset(): number {
        const pd = this.getPlantData();
        return PLANT_TAGS_ACCESS.week_start_shift_offset(pd?.tags);
    }

    /** For each person, count the number of shifts she is planned to work */
    findOverworkedIndicators(): Set<string> {
        const lines: t.IShiftPersonLineRec[] = this.state.lines;
        const lines_previous_week: t.IShiftPersonLineRec[] = this.state.lines_previous_week;
        const lines_combined = lines.concat(lines_previous_week);

        // determine start and end shifts to use for calculation
        const week_start_shift_offset = this.getWeekStartShiftOffset();

        const year = this.state.year;
        // Changes week number considering week_start_shift_offset
        // Example #1: this.state.week = 41 and week_start_shift_offset = 1 ==> week = 41
        // Example #2: this.state.week = 41 and week_start_shift_offset = -1 ==> week = 40
        // Example #3: this.state.week = 41 and week_start_shift_offset = 22 ==> week = 42
        const week = Math.floor(this.state.week + week_start_shift_offset / SHIFTS_IN_WEEK);
        // Generates shiftTag based on year, week and week_start_shift_offset
        // Example #1: year = 2020, week = 41, week_start_shift_offset = 0 ==> shiftTag = "20204100"
        // Example #2: year = 2020, week = 41, week_start_shift_offset = 1 ==> shiftTag = "20204101"
        // Example #3: year = 2020, week = 41, week_start_shift_offset = -1 ==> shiftTag = "20204020"
        const min_tag = shiftTag(year, week, positiveModulo(week_start_shift_offset, SHIFTS_IN_WEEK));
        const max_tag = shiftTag(year, week + 1, positiveModulo(week_start_shift_offset, SHIFTS_IN_WEEK));

        const counter_person_shifts: Map<string, number[]> = new Map();
        const non_work_line_types = [LINE_TYPES.non_work, LINE_TYPES.sick_leave];
        for (const line of lines_combined) {
            const line_type = LINE_TAGS_ACCESS.type(line.line_tags);
            if (non_work_line_types.indexOf(line_type as LineTypesEnum) >= 0) {
                // skip line types that denote some sort of non-work
                continue;
            }
            for (const shift of line.shifts) {
                // filter if shift should not be included into calculation
                if (shift.shift_tag < min_tag || shift.shift_tag >= max_tag) {
                    continue;
                }
                for (const person of shift.persons) {
                    const person_shifts = counter_person_shifts.get(person.uuid);
                    if (person_shifts !== undefined) {
                        person_shifts.push(shift.shift_number);
                    } else {
                        counter_person_shifts.set(person.uuid, [shift.shift_number]);
                    }
                }
            }
        }
        const overworked_people: Set<string> = new Set();
        for (const [person_uuid, shifts] of counter_person_shifts.entries()) {
            const person_shifts = new Set(shifts).size;
            const person_max_shifts = this.props.personMaxNumberShifts(person_uuid);
            if (person_max_shifts < person_shifts) {
                overworked_people.add(person_uuid);
            }
        }
        return overworked_people;
    }

    renderTags(tags: t.ITags) {
        const tag_items: React.ReactNode[] = [];
        for (const tag in tags) {
            const label =
                // first try if we have native translation
                hasTranslation(`Manufacturing.People.${tag}`)
                    ? translate(`Manufacturing.People.${tag}`)
                    : // if not, try tag friendly title
                      hasTranslation(`tag_groups.PEOPLE_TAGS.values.title.${tag.trim()}`)
                      ? translate(`tag_groups.PEOPLE_TAGS.values.title.${tag.trim()}`)
                      : // no luck, show tag
                        tag;
            const value = tags[tag];
            tag_items.push(<dt key={`${tag}_dt`}>{label}</dt>, <dd key={`${tag}_dd`}>{value}</dd>);
        }
        return tag_items;
    }

    onShowCompetences = (key: ToggleNamesCompetences) => {
        return () => {
            const value = this.state[key];
            setToggleLocalStorage(key, !value);
            this.setState({ [String(key)]: !value } as Pick<State, ToggleNamesCompetences>);
        };
    };

    renderWorkerAlert() {
        const close = () => this.setState({ show_worker_alert: false });
        return (
            <Modal show={this.state.show_worker_alert} onHide={close}>
                <Modal.Header>
                    <Modal.Title>
                        <FormattedMessage id="common.warning" defaultMessage="Warning" />
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div>
                        <FormattedMessage
                            id="ShiftDetails.unable_to_remove_worker"
                            defaultMessage="Unable to remove worker"
                        />
                    </div>
                    <div>
                        <FormattedMessage id="ShiftDetails.remove_worker_from_lines" defaultMessage="" />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button onClick={close} className="btn btn-outline-primary">
                        <FormattedMessage id="common.ok" defaultMessage="Close" />
                    </button>
                </Modal.Footer>
            </Modal>
        );
    }

    renderDisplaySettingsModal() {
        const close = () => this.setState({ show_settings_editor: false });
        return (
            <Modal show={this.state.show_settings_editor} onHide={close}>
                <Modal.Header>
                    <Modal.Title>
                        <FormattedMessage id="ShiftDetails.view_settings" defaultMessage="View settings" />
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="form-group">
                        <label>
                            <FormattedMessage id="common.show" defaultMessage="Show" />{" "}
                            {translate("ShiftDetails.Icon.competence", "C")}:{" "}
                            <FormattedMessage
                                id="ShiftDetails.IconText.competence"
                                defaultMessage="Person assigned to a line that he is unqualified for"
                            />
                        </label>
                        <br />
                        <Toggle
                            name="toggle_show_c"
                            checked={this.state.showC}
                            onChange={this.onShowCompetences("showC")}
                        />
                    </div>
                    <div className="form-group">
                        <label>
                            <FormattedMessage id="common.show" defaultMessage="Show" /> Nx :{" "}
                            <FormattedMessage
                                id="ShiftDetails.IconText.shiftNx"
                                defaultMessage="Person assigned to multiple (N) shifts within the same day"
                            />
                        </label>
                        <br />
                        <Toggle
                            name="toggle_show_nx"
                            checked={this.state.showNX}
                            onChange={this.onShowCompetences("showNX")}
                        />
                    </div>
                    <div className="form-group">
                        <label>
                            <FormattedMessage id="common.show" defaultMessage="Show" />{" "}
                            {translate("ShiftDetails.Icon.shift", "S")}:{" "}
                            <FormattedMessage
                                id="ShiftDetails.IconText.shift"
                                defaultMessage="Person assigned to invalid shift"
                            />
                        </label>
                        <br />
                        <Toggle
                            name="toggle_show_s"
                            checked={this.state.showS}
                            onChange={this.onShowCompetences("showS")}
                        />
                    </div>
                    <div className="form-group">
                        <label>
                            <FormattedMessage id="common.show" defaultMessage="Show" />{" "}
                            {translate("ShiftDetails.Icon.absent_worker_assigned", "A")}:{" "}
                            <FormattedMessage
                                id="ShiftDetails.IconText.absent_worker_assigned"
                                defaultMessage="Absent worker assigned"
                            />
                        </label>
                        <br />
                        <Toggle
                            name="toggle_show_a"
                            checked={this.state.showA}
                            onChange={this.onShowCompetences("showA")}
                        />
                    </div>
                    <div className="form-group">
                        <label>
                            <FormattedMessage id="common.show" defaultMessage="Show" />{" "}
                            {translate("ShiftDetails.Icon.reduced_workshift", "R")}:{" "}
                            <FormattedMessage
                                id="ShiftDetails.IconText.reduced_workshift"
                                defaultMessage="Assigned to too many shifts"
                            />
                        </label>
                        <br />
                        <Toggle
                            name="toggle_show_r"
                            checked={this.state.showR}
                            onChange={this.onShowCompetences("showR")}
                        />
                    </div>
                    <div className="form-group">
                        <label>
                            <FormattedMessage id="common.show_tool_change" defaultMessage="Show tool change" />
                        </label>
                        <br />
                        <Toggle
                            name="show_tool_change"
                            checked={this.state.show_tool_change}
                            onChange={this.handleCheckboxChange}
                        />
                    </div>
                    <h2>{translate("ShiftDetails.print_daily_settings")}</h2>
                    <div className="form-group">
                        <label>
                            <FormattedMessage
                                id="ShiftDetails.print_daily_show_work_orders"
                                defaultMessage="Display work-orders"
                            />
                        </label>
                        <br />
                        <Toggle
                            name="show_work_orders_in_print"
                            checked={this.state.show_work_orders_in_print}
                            onChange={this.handleCheckboxChange}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button onClick={close} className="btn btn-outline-primary">
                        <FormattedMessage id="common.close" defaultMessage="Close" />
                    </button>
                </Modal.Footer>
            </Modal>
        );
    }

    handlePoolEditorResult(res: PoolEditModalType) {
        if (res.hasConfirmed) {
            this.setState(
                prevState => {
                    const lines = prevState.lines;
                    // override line assignments for lines
                    for (const line_assign of res.assignments) {
                        for (const line of lines) {
                            if (line.line_uuid !== line_assign.line_uuid) {
                                continue;
                            }
                            const line_shifts = line.shifts.find(s => s.shift_tag === this.state.poolEditorShiftTag);
                            if (line_shifts === undefined) {
                                continue;
                            }
                            line_shifts.persons = line_assign.persons;
                        }
                    }
                    return {
                        lines,
                        showPoolEditor: false,
                        hash: res.hash,
                    };
                },
                () => {
                    this.props.onScheduleUpdate(this.getScheduleLines());
                    // save changes everytime modal pool editor is closed
                    this.saveChanges();
                }
            );
        } else {
            this.setState({ showPoolEditor: false });
        }
    }

    handleClickShiftAssignmentDetailsExportDayModal = (message: string) => {
        if (this.state.show_export_day_modal) {
            this.exportToHtmlDay(message);
        } else {
            this.startExportToHTMLWeekend(message);
        }
    };

    handleWeekPoolScheduleSave = async (updatedLines: t.IShiftPersonLineRec[]): Promise<void> => {
        const { start_shift_tag, end_shift_tag } = bl.startEndShiftTags(
            this.state.year,
            this.state.week,
            this.props.plant
        );
        const first_shift_ts = u.shiftNumber(u.fromShiftTag(start_shift_tag)).ts_start;
        const last_shift_ts = u.shiftNumber(u.fromShiftTag(end_shift_tag)).ts_end;

        const res = await getBackend().manufacturing.storePeopleShiftAssignments({
            hash: this.state.hashPooled,
            lines: mapShiftPersonLinesToStoreRecs(updatedLines),
            first_shift_ts,
            last_shift_ts,
        });

        this.pooledScheduleBaseline = u.deepClone(updatedLines);

        const updatedLineByUuid = new Map(updatedLines.map(x => [x.line_uuid, x]));
        const newLines = [...this.state.lines];
        for (const line of newLines) {
            const updatedLine = updatedLineByUuid.get(line.line_uuid);
            if (updatedLine !== undefined) {
                line.shifts = u.deepClone(updatedLine.shifts);
            }
        }

        this.setState(prev => ({
            hashPooled: res.hash,
            lines: newLines,
            lines_pooled: [...prev.lines_pooled],
            unsaved_changes: false,
        }));
    };

    handleFullScheduleRefresh = async () => {
        await this.loadData();
    };

    handleWeekPoolScheduleSaveUnsavedChanges = async () => {
        await this.saveChanges();
    };

    handleWeekPoolModalClose = () => {
        this.setState({ show_week_pool_editor: false });
    };

    render() {
        const { uses_pool, pool_line } = this.state;

        // list of available lines
        const lines: u.ILabelValuePair[] = [];
        if (this.state.lines !== undefined) {
            for (let i = 0; i < this.state.lines.length; i++) {
                let line = this.state.lines[i];
                lines.push({ label: line.line_title, value: line.line_uuid });
            }
        }

        if (!this.props.selectedPerson?.uuid) {
            return this.isLoading() ? <Loader /> : null;
        }

        // extract selected person data
        const selectedPerson = this.props.selectedPerson;
        const selected_name = selectedPerson.name;
        const selected_external_id = selectedPerson.external_id;
        const isAdjuster = selectedPerson.adjuster;
        let shift_preference = selectedPerson.shift_preference;

        let shifts: React.ReactNode = "--";
        if (shift_preference !== undefined) {
            let enabled_shifts = 0;
            shifts = shift_preference.map((val, i) => {
                let shift = i + 1;
                if (val === true) {
                    enabled_shifts += 1;
                    return (
                        <span key={i} className={"badge badge-shift badge-shift-" + shift}>
                            {shift}
                        </span>
                    );
                } else {
                    return null;
                }
            });
            if (enabled_shifts === 0) {
                shifts = "--";
            }
        }

        let shiftTable: React.ReactNode | null = null;
        if (this.state.lines) {
            shiftTable = this.isLoading() ? (
                <Loader />
            ) : this.props.template_mode ? (
                this.generateTemplateShiftTable(
                    this.state.week,
                    this.state.year,
                    this.state.uses_pool ? this.state.lines_pooled : this.state.lines_non_pooled,
                    this.state.worker_hours,
                    this.props.template
                )
            ) : (
                this.generateShiftTable(
                    this.state.week,
                    this.state.year,
                    this.state.lines_non_pooled,
                    this.state.worker_hours
                )
            );
        }
        // render the whole component
        return (
            <>
                <div id="shift-assignments-details">
                    {this.state.error && <ErrorComponent className="mt-0" msg={this.state.error} type="error" />}
                    <div className="tab-content">
                        <div className="tab-pane active d-flex flex-column" id="home">
                            <div className="d-flex">
                                <div className="button-list order-last ml-auto text-right max-width-50perc">
                                    <dl className="property-list shift-legend">
                                        {(this.state.showC ||
                                            this.state.showNX ||
                                            this.state.showS ||
                                            this.state.showR) && (
                                            <>
                                                <dt>{translate("common.legend", "Legend")}:</dt>
                                                <dd></dd>
                                            </>
                                        )}
                                        {this.state.showC && (
                                            <>
                                                <dt>
                                                    {createBullet(
                                                        "info",
                                                        translate("ShiftDetails.Icon.competence", "C"),
                                                        null,
                                                        true
                                                    )}
                                                </dt>
                                                <dd>
                                                    <FormattedMessage
                                                        id="ShiftDetails.IconText.competence"
                                                        defaultMessage="Person assigned to a line that he is unqualified for"
                                                    />
                                                </dd>
                                            </>
                                        )}
                                        {this.state.showNX && (
                                            <>
                                                <dt>{createBullet("warning", "Nx", null, true)}</dt>
                                                <dd>
                                                    <FormattedMessage
                                                        id="ShiftDetails.IconText.shiftNx"
                                                        defaultMessage="Person assigned to multiple (N) shifts within the same day"
                                                    />
                                                </dd>
                                            </>
                                        )}
                                        {this.state.showC && this.state.showNX && this.state.showS && this.state.showR}
                                        {this.state.showS && (
                                            <>
                                                <dt>
                                                    {createBullet(
                                                        "error",
                                                        translate("ShiftDetails.Icon.shift", "S"),
                                                        null,
                                                        true
                                                    )}
                                                </dt>
                                                <dd>
                                                    <FormattedMessage
                                                        id="ShiftDetails.IconText.shift"
                                                        defaultMessage="Person assigned to invalid shift"
                                                    />
                                                </dd>
                                            </>
                                        )}
                                        {this.state.showR && (
                                            <>
                                                <dt>
                                                    {createBullet(
                                                        "error",
                                                        translate("ShiftDetails.Icon.reduced_workshift", "R"),
                                                        null,
                                                        true
                                                    )}
                                                </dt>
                                                <dd>
                                                    <FormattedMessage
                                                        id="ShiftDetails.IconText.reduced_workshift"
                                                        defaultMessage="Assigned to too many shifts"
                                                    />
                                                </dd>
                                            </>
                                        )}
                                        <button
                                            className="btn btn-link"
                                            onClick={() => {
                                                this.setState({ show_settings_editor: true });
                                                return false;
                                            }}
                                        >
                                            <FormattedMessage id="Header.menu.settings" defaultMessage="Settings" />
                                            ...
                                        </button>
                                        {this.renderDisplaySettingsModal()}
                                        {this.renderWorkerAlert()}
                                    </dl>
                                    {Auth.roleCanEditShiftBoard() && (
                                        <div>
                                            {this.state.delete_mode && (
                                                <div className="d-inline shift-board-alert mr-2 mb-2">
                                                    <FormattedMessage
                                                        id="Shifts.deletion_enabled"
                                                        defaultMessage="Deletion enabled"
                                                    />
                                                </div>
                                            )}
                                            <div className="d-inline mr-2">
                                                <input
                                                    type="checkbox"
                                                    checked={this.state.delete_mode}
                                                    onChange={() => {
                                                        this.setState({ delete_mode: !this.state.delete_mode });
                                                    }}
                                                />
                                            </div>
                                            <FormattedMessage id="Shifts.deletion" defaultMessage="Deletion" />
                                        </div>
                                    )}
                                    {this.hasUnsavedChanges() && (
                                        <div>
                                            <div className="shift-board-alert mb-0">
                                                <FormattedMessage
                                                    id="Shifts.please_save"
                                                    defaultMessage="The shift schedule has changed. Please save the data."
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="order-first">
                                    <h5 className="pane-title">
                                        {selected_name}
                                        {Auth.roleCanEditShiftBoard() && (
                                            <Link to={`/digital-twin/resources/person/view/${selectedPerson.uuid}`}>
                                                <img src={img_edit} alt="edit" className="edit-icon" />
                                            </Link>
                                        )}
                                    </h5>
                                    <dl className="property-list shift-details">
                                        <dt>
                                            <FormattedMessage id="common.id" defaultMessage="ID" />
                                        </dt>
                                        <dd>{selected_external_id}</dd>
                                        {isAdjuster && (
                                            <dd>
                                                <span className="badge-list">
                                                    <span className="badge badge-primary">
                                                        <FormattedMessage
                                                            id="common.adjuster"
                                                            defaultMessage="Adjuster"
                                                        />
                                                    </span>
                                                </span>
                                            </dd>
                                        )}
                                        <dt>
                                            <FormattedMessage id="common.shifts" defaultMessage="Shifts" />
                                        </dt>
                                        <dd>
                                            <span className="badge-list">{shifts}</span>
                                        </dd>
                                        {this.renderTags(selectedPerson.tags)}
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="shift-assignments-main" data-testid="shift-assignments-main">
                    <div className="tab-content">
                        <div className="tab-pane active d-flex flex-column" id="home">
                            {shiftTable}
                            <ShiftAssignmentDetailsExportDayModal
                                show={this.state.show_export_day_modal || this.state.show_export_week_modal}
                                initialMessage={this.state.daily_message}
                                handleClick={this.handleClickShiftAssignmentDetailsExportDayModal}
                                handleCancel={() =>
                                    this.setState({ show_export_day_modal: false, show_export_week_modal: false })
                                }
                            />
                        </div>
                    </div>
                </div>
                {uses_pool &&
                    pool_line !== null &&
                    (lineGroupUsesAdvancedPoolScheduling(this.props.plant, this.props.line_group_uuid) ? (
                        <ShiftAssignmentWeekPoolModal
                            plant_uuid={this.props.plant}
                            line_group_uuid={this.props.line_group_uuid}
                            week={this.props.week}
                            year={this.props.year}
                            show={this.state.show_week_pool_editor}
                            people={this.props.people}
                            pool_line={pool_line}
                            pooled_lines={this.state.lines_pooled}
                            selected_shift={this.state.poolEditorShiftTag}
                            hasUnsavedChanges={this.hasUnsavedChanges()}
                            isLoading={this.isLoading()}
                            getPersonCompetenceLevel={this.props.getPersonCompetenceLevel}
                            onClose={this.handleWeekPoolModalClose}
                            onScheduleDataRefresh={this.handleFullScheduleRefresh}
                            onSave={this.handleWeekPoolScheduleSave}
                            onUnsavedChangesSave={this.handleWeekPoolScheduleSaveUnsavedChanges}
                            onWeekChange={this.props.onWeekChange}
                        />
                    ) : (
                        <ShiftAssignmentPoolEdit
                            show={this.state.showPoolEditor}
                            lines={this.state.lines}
                            lines_pooled={this.state.lines_pooled}
                            pool_line={pool_line}
                            shift_idx={this.state.poolEditorShift}
                            shift_tag={this.state.poolEditorShiftTag}
                            uniquePersonName={this.props.uniquePersonName}
                            hash={this.state.hash}
                            onClose={res => this.handlePoolEditorResult(res)}
                        />
                    ))}
            </>
        );
    }
}

export default ShiftAssignmentDetails;
